import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './AdminProperties.css';

function AdminProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchProperties();
  }, [user, navigate, currentPage, status]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 20
      };
      if (search) params.search = search;
      if (status !== 'all') params.status = status;

      const response = await api.get('/api/admin/properties', { params });
      setProperties(response.data.properties || []);
      setPagination(response.data.pagination);
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.properties.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProperties();
  };

  const toggleFeatured = async (propertyId, currentStatus) => {
    try {
      await api.post(`/api/admin/properties/${propertyId}/toggle-featured`);
      toast.success(currentStatus ? t('admin.properties.unfeaturedSuccess') : t('admin.properties.featuredSuccess'));
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.properties.failedToUpdate'));
    }
  };

  const deleteProperty = async (propertyId) => {
    if (!window.confirm(t('admin.properties.deleteConfirm'))) {
      return;
    }

    try {
      await api.delete(`/api/admin/properties/${propertyId}`);
      toast.success(t('admin.properties.deletedSuccess'));
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.properties.failedToDelete'));
    }
  };

  return (
    <div className="admin-properties">
      <div className="admin-properties-header">
        <div>
          <h1>{t('admin.properties.title')}</h1>
          <p>{t('admin.properties.subtitle')}</p>
        </div>
        <Link to="/admin/properties/new" className="btn btn-primary">
          + {t('admin.properties.createForHost')}
        </Link>
      </div>

      <div className="filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder={t('admin.properties.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">{t('common.search')}</button>
        </form>

        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">{t('admin.properties.allProperties')}</option>
          <option value="active">{t('admin.properties.activeOnly')}</option>
          <option value="inactive">{t('admin.properties.inactiveOnly')}</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">{t('admin.properties.loading')}</div>
      ) : (
        <>
          <div className="properties-table">
            <table>
              <thead>
                <tr>
                  <th>{t('admin.properties.property')}</th>
                  <th>{t('admin.properties.host')}</th>
                  <th>{t('admin.properties.location')}</th>
                  <th>{t('admin.properties.price')}</th>
                  <th>{t('admin.properties.status')}</th>
                  <th>{t('admin.properties.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((property) => (
                  <tr key={property.id}>
                    <td>
                      <div className="property-info">
                        {property.images && property.images.length > 0 && (
                          <img
                            src={property.images[0].image_url}
                            alt={property.title}
                            className="property-thumb"
                          />
                        )}
                        <div>
                          <div className="property-title">{property.title}</div>
                          <div className="property-type">{property.property_type}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="host-info">
                        <div>{property.host.name}</div>
                        <div className="host-email">{property.host.email}</div>
                      </div>
                    </td>
                    <td>{property.city}, {property.state}</td>
                    <td>R$ {property.price_per_night}{t('admin.properties.night')}</td>
                    <td>
                      <span className={`status-badge ${property.is_active ? 'active' : 'inactive'}`}>
                        {property.is_active ? t('admin.properties.active') : t('admin.properties.inactive')}
                      </span>
                      {property.is_featured && (
                        <span className="status-badge featured">{t('admin.properties.featured')}</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link
                          to={`/admin/properties/${property.id}/edit`}
                          className="btn-icon"
                          title={t('admin.properties.edit')}
                        >
                          ✏️
                        </Link>
                        <button
                          onClick={() => toggleFeatured(property.id, property.is_featured)}
                          className="btn-icon"
                          title={property.is_featured ? t('admin.properties.unfeature') : t('admin.properties.feature')}
                        >
                          {property.is_featured ? '⭐' : '☆'}
                        </button>
                        <button
                          onClick={() => deleteProperty(property.id)}
                          className="btn-icon btn-danger"
                          title={t('admin.properties.delete')}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {properties.length === 0 && (
              <div className="no-results">{t('admin.properties.noProperties')}</div>
            )}
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                {t('common.previous')}
              </button>
              <span>
                {t('property.page')} {pagination.page} {t('property.of')} {pagination.pages}
              </span>
              <button
                disabled={currentPage === pagination.pages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminProperties;
